import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Mail, Loader2 } from 'lucide-react';

export function AuthForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'error' | 'success'>('error');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (error) {
        if (error.message === 'Invalid login credentials') {
          setMessage('Invalid email or password. Please try again.');
        } else {
          setMessage(error.message);
        }
        setMessageType('error');
        return;
      }
      
      navigate('/projects');
    } catch (error: any) {
      setMessage('An unexpected error occurred. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-teal/5 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <div className="bg-brand-teal/10 p-3 rounded-full">
            <Mail className="w-8 h-8 text-brand-teal" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-center text-brand-teal mb-8">
          Welcome to TeamUpdraft
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email address
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
              placeholder="••••••••"
              minLength={6}
            />
          </div>
          {message && (
            <p className={`text-sm ${
              messageType === 'error' ? 'text-red-600' : 'text-green-600'
            } text-center`}>
              {message}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-orange text-white py-2 px-4 rounded-lg hover:bg-brand-orange-light focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                Signing in...
              </span>
            ) : (
              'Sign in'
            )}
          </button>
        </form>
        <div className="mt-4 text-center">
          <Link 
            to="/reset-password"
            className="text-sm text-brand-orange hover:text-brand-orange-light transition-colors"
          >
            Forgot your password?
          </Link>
        </div>
        <p className="mt-4 text-center text-sm text-gray-600">
          This is an invite-only application. Please contact your administrator for access.
        </p>
      </div>
    </div>
  );
}